import { NavLink, useLocation } from 'react-router-dom';
import { PanelLeftClose, PanelLeftOpen, Radio, X } from 'lucide-react';
import { cn } from '@/lib/cn';
import { useRealtime } from '@/lib/socket';
import { useAuth } from '@/stores/auth.store';
import { useUiStore } from '@/stores/ui.store';
import { usePendingApprovalsCount } from '@/features/approvals/api';
import { AGENCY_NAV } from './nav';

interface SidebarProps {
  mobileOpen: boolean;
  onCloseMobile: () => void;
}

export function Sidebar({ mobileOpen, onCloseMobile }: SidebarProps) {
  const { can } = useAuth();
  const collapsed = useUiStore((state) => state.sidebarCollapsed);
  const toggle = useUiStore((state) => state.toggleSidebar);
  const { pathname } = useLocation();
  const { data: pending } = usePendingApprovalsCount();
  const { connected } = useRealtime();

  const groups = AGENCY_NAV.map((group) => ({
    ...group,
    items: group.items.filter((item) => can(item.permissions)),
  })).filter((group) => group.items.length > 0);

  const content = (
    <div className="flex h-full flex-col">
      <div
        className={cn(
          'flex h-14 shrink-0 items-center gap-2 border-b border-line px-3',
          collapsed ? 'justify-center' : 'justify-between',
        )}
      >
        {!collapsed && (
          <span className="flex items-center gap-2 truncate">
            <span
              aria-hidden
              className="flex h-7 w-7 items-center justify-center rounded-lg bg-primary text-xs font-bold text-primary-fg"
            >
              MO
            </span>
            <span className="truncate text-sm font-semibold text-fg">Marketing OS</span>
          </span>
        )}
        <button
          type="button"
          onClick={toggle}
          aria-label={collapsed ? 'Expandir menu lateral' : 'Recolher menu lateral'}
          className="hidden rounded-md p-1.5 text-muted transition hover:bg-elevated hover:text-fg lg:block"
        >
          {collapsed ? <PanelLeftOpen className="h-4 w-4" /> : <PanelLeftClose className="h-4 w-4" />}
        </button>
        <button
          type="button"
          onClick={onCloseMobile}
          aria-label="Fechar menu"
          className="rounded-md p-1.5 text-muted transition hover:bg-elevated hover:text-fg lg:hidden"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      <nav aria-label="Navegação principal" className="flex-1 overflow-y-auto px-2 py-3">
        {groups.length === 0 && !collapsed && (
          <p className="px-2 py-4 text-xs text-muted">
            Seu perfil não tem acesso a nenhum módulo. Fale com o administrador.
          </p>
        )}
        {groups.map((group) => (
          <div key={group.id} className="mb-4 last:mb-0">
            {!collapsed && (
              <p className="px-2 pb-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted">
                {group.label}
              </p>
            )}
            <ul className="space-y-0.5">
              {group.items.map((item) => {
                const Icon = item.icon;
                const active = item.matchPrefix
                  ? pathname.startsWith(item.matchPrefix)
                  : pathname === item.to;
                const badge = item.to === '/aprovacoes' ? (pending?.count ?? 0) : 0;

                return (
                  <li key={item.to}>
                    <NavLink
                      to={item.to}
                      onClick={onCloseMobile}
                      title={collapsed ? item.label : undefined}
                      aria-current={active ? 'page' : undefined}
                      className={cn(
                        'flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm transition',
                        collapsed && 'justify-center px-0',
                        active
                          ? 'bg-primary-soft font-medium text-primary'
                          : 'text-muted hover:bg-elevated hover:text-fg',
                      )}
                    >
                      <Icon className="h-4 w-4 shrink-0" aria-hidden />
                      {!collapsed && <span className="truncate">{item.label}</span>}
                      {!collapsed && badge > 0 && (
                        <span className="ml-auto rounded-full bg-warning-soft px-1.5 py-0.5 text-[10px] font-bold text-warning">
                          {badge}
                        </span>
                      )}
                    </NavLink>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </nav>

      <div className={cn('border-t border-line px-3 py-2.5 text-xs text-muted', collapsed && 'text-center')}>
        <span className="flex items-center gap-1.5" title={connected ? 'Tempo real conectado' : 'Tempo real desconectado'}>
          <Radio
            className={cn('h-3.5 w-3.5 shrink-0', connected ? 'text-success' : 'text-muted')}
            aria-hidden
          />
          {!collapsed && <span>{connected ? 'Tempo real ativo' : 'Tempo real offline'}</span>}
        </span>
      </div>
    </div>
  );

  return (
    <>
      {/* Desktop */}
      <aside
        className={cn(
          'hidden shrink-0 border-r border-line bg-surface transition-all lg:block',
          collapsed ? 'w-16' : 'w-60',
        )}
      >
        {content}
      </aside>

      {/* Mobile: drawer */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-slate-950/60 animate-fade-in" onClick={onCloseMobile} aria-hidden />
          <aside
            role="dialog"
            aria-modal="true"
            aria-label="Menu de navegação"
            className="absolute inset-y-0 left-0 w-64 border-r border-line bg-surface shadow-pop"
          >
            {content}
          </aside>
        </div>
      )}
    </>
  );
}
